import React, { useCallback, useEffect, useState } from 'react'

import { t } from '@/i18n'
import {
  AllCarPositionsQuery,
  CarPositionNode,
  useAllCarPositionsQuery,
} from '@/apollo/graphql'
import LoadingIndicator from '@/assets/images/loading_indicator.gif'
import { IPageInfo } from '@/types'
import { getFetchMoreStatus } from '@/utils'
import { InfiniteLoader } from '@/components'

import HistoryItem from './historyItem'
import { CarPositionButton } from '@/components/buttons/CarPositionButton/CarPositionButton'

interface Props {
  carId: string
  id: string
}

const pageSize = 20

const initialPageInfo: IPageInfo = {
  hasNextPage: false,
  offset: 0,
  fetchMoreStatus: 'loading',
}

const History: React.FC<Props> = ({ carId, id }) => {
  const [data, setData] = useState<Partial<CarPositionNode>[]>([])
  const [pageInfo, setPageInfo] = useState<IPageInfo>(initialPageInfo)

  const setResData = useCallback((res: AllCarPositionsQuery, reset = false) => {
    if (res.car?.positions?.edges) {
      const d: Partial<CarPositionNode>[] = []
      res.car.positions.edges.forEach(e => {
        // @ts-ignore
        if (e?.node) d.push(e.node)
      })
      if (reset) {
        setData(d)
      } else {
        setData(prev => [...prev, ...d])
      }
    }
  }, [])

  const { loading, networkStatus, refetch } = useAllCarPositionsQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      id,
      offset: pageInfo?.offset || 0,
      first: pageSize,
    },
    onCompleted: res => {
      setResData(res)
      if (res.car?.positions?.edges) {
        setPageInfo(prev => ({
          ...prev,
          hasNextPage: res.car?.positions?.pageInfo.hasNextPage,
        }))
      }
    },
  })

  const onRefetch = useCallback(async () => {
    const res = await refetch()
    setResData(res.data, true)
    if (res.data.car?.positions?.edges) {
      setPageInfo({
        ...initialPageInfo,
        hasNextPage: res.data.car?.positions?.pageInfo.hasNextPage,
      })
    }
  }, [refetch, setResData])

  useEffect(() => {
    setPageInfo(prev => ({
      ...prev,
      fetchMoreStatus: getFetchMoreStatus({
        networkStatus,
        hasNextPage: prev?.hasNextPage,
        withForce: true,
      }),
    }))
  }, [networkStatus])

  return (
    <div className="column gap">
      <CarPositionButton carId={carId} onSuccess={onRefetch} />
      <InfiniteLoader
        fetchMoreStatus={pageInfo.fetchMoreStatus}
        onFetchMore={() =>
          setPageInfo(prev => ({ ...prev, offset: data.length }))
        }
      >
        <table>
          <thead>
            <tr>
              <th>{t('delivery_time')}</th>
              <th>{t('return_time')}</th>
              <th>{t('location')}</th>
              <th>{t('driver')}</th>
              <th>{t('customer')}</th>
              <th>{t('deal_id')}</th>
              <th>{t('action')}</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr className="history-loading">
                <td colSpan={7}>
                  <img src={LoadingIndicator} />
                </td>
              </tr>
            )}
            {!loading && !data.length && (
              <tr className="history-empty">
                <td colSpan={7}>{t('noData')}</td>
              </tr>
            )}
            {data.map((datum, index) => (
              <HistoryItem
                key={index}
                carId={carId}
                data={datum}
                refetch={onRefetch}
              />
            ))}
          </tbody>
        </table>
      </InfiniteLoader>
    </div>
  )
}

export default History
